<template>
  <div>
    <page-header-narrow :header-text="pageHeader">
      <div class="heading-actions flex-right">
        <div class="heading-action">
          <delete-button
            v-if="isEdit"
            modal-id="custom-workout-delete-modal"
            modal-heading="Delete Custom Workout?"
            modal-text="Are you sure you want to delete this custom workout? All 'Workout Complete' stats will be kept, but all incomplete workouts and the workout itself will be removed and no longer be able to be scheduled."
            label="Delete Workout"
            classes="-delete -ico-txt"
            :submitting="deleting"
            @delete-action="handleDelete"
          />
        </div>
      </div>
    </page-header-narrow>

    <!-- edit -->
    <div class="container -narrow">
      <vee-form v-slot="{ validate }" as="">
        <form ref="formRef" method="post" novalidate @submit.prevent="handleSubmit(validate)">
          <input v-if="isEdit" name="_method" type="hidden" value="PUT" />

          <div class="standard-form flex flex-wrap">
            <div class="flex-half">
              <fieldset class="standard-fieldset">
                <legend class="heading -small">Basic Information</legend>
                <text-input
                  v-model="formData.title"
                  input-id="title"
                  label="Title"
                  placeholder="My Custom Workout"
                  rules="required|max:120"
                  validate-as="First name"
                  maxlength="120"
                />
                <textarea-input
                  v-model="formData.body"
                  input-id="body"
                  label="Description"
                  placeholder="Details for my custom workout"
                  :large="true"
                />
                <text-input
                  v-model="formData.link_url"
                  input-id="link_url"
                  label="Link URL"
                  placeholder="https://example.org"
                  rules="url"
                  validate-as="Link"
                />
                <select-input
                  v-model="formData.difficulty"
                  input-id="difficulty"
                  label="Difficulty level"
                  rules="required"
                  :options="difficultyOptions"
                />
                <text-input
                  v-model="formData.minutes"
                  input-id="minutes"
                  type="number"
                  label="Duration (Minutes)"
                  placeholder="Number of minutes. EX: 60"
                  rules="required|numeric|min_value:1"
                  validate-as="Minutes"
                  min="1"
                  step="1"
                />
              </fieldset>
            </div>
            <div class="flex-half flex-right">
              <form-fieldset-slot legend="Training Types" input-name="training_type[]">
                <div class="flex flex-wrap">
                  <div class="flex-half">
                    <checkbox-input
                      v-for="trainingType in trainingTypeSplit.left"
                      :key="trainingType.id"
                      v-model="formData.training_type"
                      :input-id="`training_type_${trainingType.id}`"
                      name="training_type[]"
                      :label="trainingType.name"
                      :checked-value="parseInt(trainingType.id)"
                      unchecked-value=""
                      rules="required"
                      validate-as="Training type"
                      :show-error-msg="false"
                    />
                  </div>
                  <div class="flex-half flex-right">
                    <checkbox-input
                      v-for="trainingType in trainingTypeSplit.right"
                      :key="trainingType.id"
                      v-model="formData.training_type"
                      :input-id="`training_type_${trainingType.id}`"
                      name="training_type[]"
                      :label="trainingType.name"
                      :checked-value="parseInt(trainingType.id)"
                      unchecked-value=""
                      rules="required"
                      validate-as="Training type"
                      :show-error-msg="false"
                    />
                  </div>
                </div>
              </form-fieldset-slot>

              <form-fieldset-slot legend="Body Focus" input-name="body_focus[]">
                <checkbox-input
                  v-for="bodyFocus in bodyFocuses"
                  :key="bodyFocus.id"
                  v-model="formData.body_focus"
                  :input-id="`body_focus_${bodyFocus.id}`"
                  name="body_focus[]"
                  :label="bodyFocus.name"
                  :checked-value="parseInt(bodyFocus.id)"
                  unchecked-value=""
                  rules="required"
                  validate-as="Body focus"
                  :show-error-msg="false"
                />
              </form-fieldset-slot>
            </div>
          </div>
          <div class="form-actions">
            <div class="form-action">
              <csrf-input />
              <submit-button
                :submitting="processing"
                :label="button"
                submitting-label="Saving…"
                classes="-main -large-medium-only"
                @submit-action="handleSubmit(validate)"
              />
            </div>
            <div class="form-action">
              <button type="button" class="btn -link -large-medium-only" @click.prevent="cancel">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </vee-form>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { Form as VeeForm } from 'vee-validate'
import { initExtraValidationRules } from '@/utils/forms/validation-rules'
import TextInput from '@/components/forms/text-input.vue'
import TextareaInput from '@/components/forms/textarea-input.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'
import SelectInput from '@/components/forms/select-input.vue'
import FormFieldsetSlot from '@/components/forms/form-fieldset-slot.vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import DeleteButton from '@/components/buttons/delete-button.vue'
import PageHeaderNarrow from '@/components/headers/page-header-narrow.vue'

export default defineComponent({
  name: 'CustomWorkoutForm',
  components: {
    VeeForm,
    TextInput,
    TextareaInput,
    SelectInput,
    CheckboxInput,
    FormFieldsetSlot,
    CsrfInput,
    SubmitButton,
    DeleteButton,
    PageHeaderNarrow,
  },
  props: {
    heading: {
      type: String,
      default: '',
    },
    bodyFocuses: {
      type: Object, // EX: { id: value}
      default: () => ({}),
    },
    trainingTypes: {
      type: Object, // EX: { id: value}
      default: () => ({}),
    },
    // used only for edit
    customWorkout: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isEdit: !!(this.customWorkout && this.customWorkout.id),
      processing: false,
      deleting: false,
      difficultyOptions: [
        { value: '', label: 'Select One' },
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
      ],
      formData: {
        // defaults
        difficulty: '',
        training_type: [],
        body_focus: [],
        // edit
        ...this.customWorkout,
      },
    }
  },
  computed: {
    pageHeader() {
      return this.isEdit ? 'Edit Custom Workout' : 'Create Custom Workout'
    },
    button() {
      return this.isEdit ? 'Save Changes' : 'Create Workout'
    },

    // used to show training types in two columns
    trainingTypeSplit() {
      const half = Math.ceil(this.trainingTypes.length / 2)

      return this.trainingTypes.reduce(
        (acc, trainingType, index) => {
          if (index < half) {
            acc.left[index] = {
              id: trainingType.id,
              name: trainingType.name,
            }
          } else {
            acc.right[index] = {
              id: trainingType.id,
              name: trainingType.name,
            }
          }

          return acc
        },
        { left: {}, right: {} },
      )
    },
  },

  created() {
    initExtraValidationRules()
  },

  // instance methods
  methods: {
    cancel() {
      // go back to detail view
      if (
        document.referrer &&
        document.referrer.includes(`${this.customWorkout.id}/${this.customWorkout.seo_url}`)
      ) {
        return (window.location.href = `/my/custom-workouts/${this.customWorkout.id}/${this.customWorkout.seo_url}`)
      }

      // otherwise go to list view
      window.history.back()
    },

    async handleSubmit(validate) {
      if (this.processing) {
        return
      }

      this.processing = true

      // update url to force http
      if (this.formData.link_url && !/(http(s?)):\/\//gi.test(this.formData.link_url)) {
        this.formData.link_url = `https://${this.formData.link_url}`
      }

      try {
        const results = await validate()

        // invalid, stop
        if (!results.valid) {
          this.processing = false
          return
        }

        // traditional form submit
        if (this.$refs.formRef instanceof HTMLFormElement) {
          // wait until next tick to make sure v-model update
          await this.$nextTick()
          this.$refs.formRef.submit()
        }
      } catch (e) {
        // if error thrown, enable form
        this.processing = false
      }
    },

    handleDelete() {
      this.deleting = true
      this.processing = true // do not allow form submits while deleting
      window.location.href = `/my/custom-workouts/remove/${this.customWorkout.id}`
    },
  },
})
</script>
